import { useId } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "react-tooltip";
import type { IconNames } from "mapiq-atoms";

import { Icon } from "@atoms";
import { cn } from "@utils";

type PersonRelationIconsProps = {
  // What's shown before the person
  icon: IconNames;
  label: string;
  isBusy?: boolean;
  // Makes it a button. Without it, it only shows the relation.
  onClick?: () => void;
};

const ICONS_CLASS_NAME = "flex h-7 items-center gap-0.5 rounded-md px-2 transition-colors";

export const PersonRelationIcons = ({ icon, label, isBusy = false, onClick }: PersonRelationIconsProps) => {
  const tooltipId = useId();

  const icons = (
    <>
      <Icon
        aria-hidden
        icon={icon}
        size="15px"
        color="currentColor"
      />
      <Icon
        aria-hidden
        icon="user"
        size="17px"
        color="currentColor"
      />
    </>
  );

  // In the body, because the details panel cuts off what goes past its edges. It finds its element by id.
  const tooltip = createPortal(
    <Tooltip
      id={tooltipId}
      content={label}
      place="top"
      positionStrategy="fixed"
      delayShow={250}
      openEvents={{ mouseenter: true, focus: true }}
      closeEvents={{ mouseleave: true, blur: true }}
      style={{ zIndex: 99 }}
    />,
    document.body,
  );

  if (!onClick) {
    return (
      <>
        <span
          role="img"
          aria-label={label}
          data-tooltip-id={tooltipId}
          className={cn(ICONS_CLASS_NAME, "bg-mapiq-black-800*/[0.04] text-mapiq-black-800*")}>
          {icons}
        </span>
        {tooltip}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        aria-label={label}
        aria-busy={isBusy}
        data-tooltip-id={tooltipId}
        disabled={isBusy}
        className={cn(ICONS_CLASS_NAME, "bg-mapiq-black-800* text-white hover:bg-mapiq-black-700 disabled:opacity-50")}
        onClick={onClick}>
        {icons}
      </button>
      {tooltip}
    </>
  );
};
